/**
 * <collection-search>
 * -------------------
 * Client-side full-text search across the ENTIRE collection — including products
 * on later infinite-scroll pages that are not yet in the DOM.
 *
 * Strategy:
 *  - On first focus, lazily fetch a TEXT-ONLY index of every product of the
 *    collection via the `collection.search-index` alternate template
 *    (`?view=search-index`), which returns compact JSON: handle + haystack
 *    (title + vendor + type + tags). The template paginates by 250 (Shopify's
 *    hard per-page ceiling), so a collection larger than that spans multiple
 *    pages — page 1 reports `pages`, the rest are fetched in parallel so search
 *    covers the WHOLE collection.
 *  - Cards are fetched only for the products that MATCH, one request each, via
 *    `templates/product.search-card.liquid`, in batches of BATCH_SIZE with a
 *    "show more" button. Rendering all cards up front (the previous approach)
 *    meant megabytes of Liquid per request, which intermittently timed out with
 *    a Shopify 500 and left the index empty — every query then claimed "no
 *    matches", even for products sitting in the collection.
 *  - Index pages are retried once and failures are isolated: one bad page no
 *    longer wipes the whole index. If the index cannot be loaded at all, the
 *    empty state says so instead of pretending there are no matches, and always
 *    offers a link to the store-wide search.
 *  - The original server-rendered grid (page 1) is NEVER destroyed. Search results
 *    render into a SEPARATE sibling <motion-list>; we only toggle `display` between
 *    the two, so the InfiniteButton (assets/collection.js) keeps working when the
 *    query is cleared.
 *
 * Reuses: theme.utils.debounce, AbortController, the MotionList `reload()` hook.
 */
if (!customElements.get('collection-search')) {
  customElements.define(
    'collection-search',
    class CollectionSearch extends HTMLElement {
      constructor() {
        super();
        this.indexPromise = null;
        this.items = null;
        this.indexFailed = false;
        this.active = false;
        this.cardCache = new Map();
        this.matches = [];
        this.renderedCount = 0;
      }

      static get BATCH_SIZE() {
        return 24;
      }

      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.clearButton = this.querySelector('.collection-search__clear');
        this.status = this.querySelector('.collection-search__status');
        if (!this.input) return;

        this.gridContainer = document.getElementById('ProductGridContainer');
        this.originalGrid = this.gridContainer?.querySelector('motion-list');
        if (!this.gridContainer || !this.originalGrid) return;

        this.collectionUrl = this.getAttribute('data-collection-url') || window.location.pathname;
        this.searchUrl = this.getAttribute('data-search-url') || '/search';

        // Sibling container that holds search results (created once, kept empty/hidden).
        this.resultsGrid = document.createElement('motion-list');
        this.resultsGrid.id = 'CollectionSearchResults';
        this.resultsGrid.className = this.originalGrid.className;
        this.resultsGrid.setAttribute('hidden', '');

        // Visible result count (created once, kept hidden).
        this.countLine = document.createElement('p');
        this.countLine.className = 'collection-search__count text-base lg:text-lg';
        this.countLine.setAttribute('role', 'status');
        this.countLine.setAttribute('hidden', '');

        // Look the theme's pagination up BEFORE appending our own nodes: the
        // "show more" wrapper below reuses the `pagination` class for styling and
        // would otherwise be the first `.pagination` match in this container.
        this.pagination = this.gridContainer.querySelector('.pagination');

        this.emptyState = this.buildEmptyState();
        this.moreButton = this.buildMoreButton();

        // IMPORTANT: append AFTER the original grid. collection.js's InfiniteButton
        // targets the FIRST <motion-list> in #ProductGridContainer via querySelector;
        // the original grid must stay first or infinite scroll appends into our hidden
        // results container instead (button flickers, no products load).
        this.gridContainer.append(this.countLine, this.resultsGrid, this.moreButton, this.emptyState);

        this.input.addEventListener('focus', this.prefetch.bind(this), { once: true });
        this.input.addEventListener('input', theme.utils.debounce(this.onInput.bind(this), 250));
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.clear();
        });
        this.clearButton?.addEventListener('click', this.clear.bind(this));
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.cardController?.abort();
      }

      /* ---------- data ---------- */

      prefetch() {
        if (this.indexPromise) return this.indexPromise;

        this.abortController = new AbortController();
        this.setAttribute('loading', '');

        this.indexPromise = this.fetchAllItems(this.abortController.signal)
          .then((items) => {
            this.items = items;
            this.indexFailed = false;
            this.removeAttribute('loading');
            return items;
          })
          .catch((error) => {
            this.removeAttribute('loading');
            this.indexPromise = null; // allow a retry on the next keystroke
            if (error.name !== 'AbortError') {
              this.indexFailed = true;
              console.error('[collection-search]', error);
            }
            return [];
          });

        return this.indexPromise;
      }

      // Fetch the text index of EVERY product of the collection, walking past the
      // 250-per-page paginate limit. Page 1 reports the total page count; the rest
      // are fetched in parallel and concatenated in collection order. A page that
      // fails after its retry is skipped with a warning rather than discarding the
      // pages that did load — only a failing page 1 counts as "index unavailable".
      async fetchAllItems(signal) {
        const first = await this.fetchIndexPage(1, signal);
        let items = first.items;

        const pages = parseInt(first.pages, 10) || 1;
        if (pages > 1) {
          const rest = await Promise.allSettled(
            Array.from({ length: pages - 1 }, (_, i) => this.fetchIndexPage(i + 2, signal))
          );

          rest.forEach((result, i) => {
            if (result.status === 'fulfilled') {
              items = items.concat(result.value.items);
            } else if (result.reason?.name !== 'AbortError') {
              console.warn(`[collection-search] index page ${i + 2} unavailable`, result.reason);
            }
          });
        }

        return items;
      }

      // One index page as [{ handle, terms }], retried once on failure.
      async fetchIndexPage(page, signal, attempt = 0) {
        const url = new URL(this.collectionUrl, window.location.origin);
        url.searchParams.set('view', 'search-index');
        if (page > 1) url.searchParams.set('page', page);

        try {
          const response = await fetch(url.toString(), { signal });
          if (!response.ok) throw new Error(`HTTP ${response.status} for ${url.pathname}${url.search}`);

          const data = JSON.parse(await response.text());
          if (!Array.isArray(data.items)) throw new Error('malformed search index');

          return {
            pages: data.pages,
            items: data.items.map((item) => ({ handle: item.h, terms: this.normalize(item.s || '') })),
          };
        } catch (error) {
          if (error.name === 'AbortError' || attempt >= 1) throw error;
          return this.fetchIndexPage(page, signal, attempt + 1);
        }
      }

      // The card for one matching product, from the collection-scoped product URL
      // so `within: collection` links match the browse grid. Cached per handle;
      // a card that fails to load is skipped instead of breaking the result set.
      fetchCard(handle) {
        if (this.cardCache.has(handle)) return Promise.resolve(this.cardCache.get(handle));

        const base = this.collectionUrl.replace(/\/$/, '');
        const url = new URL(`${base}/products/${encodeURIComponent(handle)}`, window.location.origin);
        url.searchParams.set('view', 'search-card');

        return fetch(url.toString(), { signal: this.cardController?.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status} for ${handle}`);
            return response.text();
          })
          .then((html) => {
            // Only a TOP-LEVEL card counts: if the `search-card` view were missing,
            // Shopify would serve the full product page, whose recommendations
            // contain cards for entirely different products.
            const body = new DOMParser().parseFromString(html, 'text/html').body;
            const card = Array.from(body.children).find((element) => element.matches('.card')) || null;
            if (card) this.cardCache.set(handle, card);
            return card;
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.warn('[collection-search]', error);
            return null;
          });
      }

      /* ---------- input ---------- */

      async onInput() {
        const raw = this.input.value.trim();
        this.clearButton?.toggleAttribute('hidden', raw.length === 0);

        if (raw.length === 0) {
          this.deactivate();
          return;
        }

        const items = await this.prefetch();
        // A newer keystroke may have superseded this one while awaiting.
        if (this.input.value.trim() !== raw) return;

        if (this.indexFailed) {
          this.activate([], raw, true);
          return;
        }

        const tokens = this.normalize(raw).split(/\s+/).filter(Boolean);
        const matches = items.filter((item) => tokens.every((token) => item.terms.includes(token)));
        this.activate(matches, raw);
      }

      activate(matches, query, unavailable = false) {
        // Drop the card requests of the query this one replaces — a broad first
        // keystroke can otherwise leave a batch of them in flight for nothing.
        this.cardController?.abort();
        this.cardController = new AbortController();

        this.active = true;
        this.query = query;
        this.matches = matches;
        this.renderedCount = 0;

        this.originalGrid.style.display = 'none';
        if (this.pagination) this.pagination.style.display = 'none';

        this.resultsGrid.innerHTML = '';
        this.moreButton.setAttribute('hidden', '');

        if (matches.length === 0) {
          this.resultsGrid.setAttribute('hidden', '');
          this.countLine.setAttribute('hidden', '');
          this.showEmptyState(query, unavailable);
          this.announce(this.emptyHeading.textContent);
          return;
        }

        this.emptyState.setAttribute('hidden', '');
        this.resultsGrid.removeAttribute('hidden');

        const countText = (this.getAttribute('data-text-results') || '%%COUNT%%').replace('%%COUNT%%', matches.length);
        this.countLine.textContent = countText;
        this.countLine.removeAttribute('hidden');
        this.announce(countText);

        this.renderNextBatch();
      }

      // Render the next slice of matches; their cards are fetched in parallel.
      async renderNextBatch() {
        const query = this.query;
        const slice = this.matches.slice(this.renderedCount, this.renderedCount + CollectionSearch.BATCH_SIZE);
        if (slice.length === 0) return;

        this.moreButton.setAttribute('hidden', '');
        this.setAttribute('loading', '');

        const cards = await Promise.all(slice.map((item) => this.fetchCard(item.handle)));

        this.removeAttribute('loading');
        // A newer query took over while the cards were in flight.
        if (!this.active || this.query !== query) return;

        const fragment = document.createDocumentFragment();
        cards.forEach((card) => {
          if (card) fragment.appendChild(card.cloneNode(true));
        });
        this.resultsGrid.appendChild(fragment);
        this.resultsGrid.reload?.();

        this.renderedCount += slice.length;
        this.moreButton.toggleAttribute('hidden', this.renderedCount >= this.matches.length);
      }

      deactivate() {
        if (!this.active) return;
        this.cardController?.abort();
        this.active = false;
        this.query = '';

        this.resultsGrid.setAttribute('hidden', '');
        this.resultsGrid.innerHTML = '';
        this.emptyState.setAttribute('hidden', '');
        this.countLine.setAttribute('hidden', '');
        this.moreButton.setAttribute('hidden', '');
        this.matches = [];
        this.renderedCount = 0;

        this.originalGrid.style.removeProperty('display');
        if (this.pagination) this.pagination.style.removeProperty('display');
        this.announce('');
      }

      clear() {
        this.input.value = '';
        this.clearButton?.setAttribute('hidden', '');
        this.deactivate();
        this.input.focus();
      }

      /* ---------- empty state ---------- */

      buildEmptyState() {
        const wrapper = document.createElement('div');
        wrapper.className = 'empty-state collection-search__empty grid items-center justify-center text-center gap-7d5';
        wrapper.setAttribute('hidden', '');

        const inner = document.createElement('div');
        inner.className = 'flex flex-col items-center gap-3';

        const heading = document.createElement('p');
        heading.className = 'empty-state__title heading leading-none tracking-tight';

        const subtext = document.createElement('p');

        inner.append(heading, subtext);

        const actions = document.createElement('div');
        actions.className = 'flex flex-col items-center gap-3';

        // Products that are simply NOT in this collection can only be found by the
        // store-wide search — offer it right here instead of dead-ending.
        const searchAll = document.createElement('a');
        searchAll.className = 'button button--primary';
        searchAll.append(this.buttonFill(), this.buttonText(this.getAttribute('data-text-search-all') || ''));

        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'button button--secondary';
        reset.append(this.buttonFill(), this.buttonText(this.getAttribute('data-text-reset') || ''));
        reset.addEventListener('click', this.clear.bind(this));

        actions.append(searchAll, reset);
        wrapper.append(inner, actions);

        this.emptyHeading = heading;
        this.emptySubtext = subtext;
        this.searchAllLink = searchAll;
        return wrapper;
      }

      buildMoreButton() {
        const wrapper = document.createElement('div');
        wrapper.className = 'pagination flex flex-wrap justify-center align-self-center md:gap-2 text-sm md:text-base';
        wrapper.id = 'CollectionSearchMore';
        wrapper.setAttribute('hidden', '');

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--primary button--fixed';
        button.append(this.buttonFill(), this.buttonText(this.getAttribute('data-text-show-more') || ''));
        button.addEventListener('click', () => this.renderNextBatch());

        wrapper.appendChild(button);
        return wrapper;
      }

      // Mirror the theme button structure (btn-fill + btn-text) so it picks up the styling.
      buttonFill() {
        const fill = document.createElement('span');
        fill.className = 'btn-fill';
        fill.setAttribute('data-fill', '');
        return fill;
      }

      buttonText(value) {
        const text = document.createElement('span');
        text.className = 'btn-text';
        text.textContent = value;
        return text;
      }

      showEmptyState(query, unavailable = false) {
        this.emptyHeading.textContent = unavailable
          ? this.getAttribute('data-text-unavailable') || ''
          : this.fillTerms(this.getAttribute('data-text-no-results'), query);
        this.emptySubtext.textContent = unavailable ? '' : this.getAttribute('data-text-no-results-sub') || '';

        const url = new URL(this.searchUrl, window.location.origin);
        url.searchParams.set('q', query);
        this.searchAllLink.href = url.toString();

        this.emptyState.removeAttribute('hidden');
      }

      /* ---------- helpers ---------- */

      fillTerms(template, query) {
        return (template || '').replace('%%TERMS%%', query);
      }

      announce(message) {
        if (this.status) this.status.textContent = message;
      }

      // Lowercase + strip German diacritics (ä→a, ö→o, ü→u, ß→ss) for forgiving matching.
      // The combining-marks range (U+0300–U+036F) is built via RegExp to keep the source ASCII.
      normalize(value) {
        return value
          .replace(/ß/g, 'ss')
          .normalize('NFD')
          .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
          .toLowerCase();
      }
    }
  );
}
